import React, { createContext, useContext, useMemo } from 'react'

import { useFeatureSwitches } from 'hooks/api/useFeatures'
import { useIsInternalUser } from 'hooks/useIsInternalUser'

import { brandGlossaryFor, glossaryFor, glossaryTerm, type GlossaryEntry } from './glossary'

/**
 * Which glossary corpus the current reader gets.
 *
 * ## Why this is a context and not a hook call at each site
 *
 * "Which terms may this person see" has ONE answer per render, and it is a property of
 * the viewer, not of the tooltip. Resolving it at each call site would mean every leaf
 * that explains a word — `InfoTooltip` is used in dozens of places — grows a dependency
 * on auth and on the Vera-space store, so a presentational component could no longer be
 * rendered without them. Answering it once, high up, keeps the leaves presentational and
 * makes the audience boundary a single readable decision.
 *
 * ## The default is the BRAND corpus, deliberately
 *
 * A component rendered outside this provider gets `GLOSSARY` — exactly what every call
 * site got before this file existed. That makes the failure mode of a missing provider
 * "an internal term does not render", which is visible and harmless, rather than "an
 * internal term renders for a brand", which is the leak §43 and §44 exist to prevent.
 * Failing closed is the only acceptable direction for an audience gate.
 *
 * ## Staff inside a brand's space get the brand corpus, and that is correct
 *
 * `useIsInternalUser` is false while a staffer is operating inside a company's space
 * (see its own docblock). That is the behaviour we want rather than an accident of reuse:
 * in a space you are looking at what the brand looks at, so the vocabulary should be the
 * brand's too. An internal-only definition surfacing there would describe machinery the
 * surface in front of you does not have.
 */
// VRG-680 — and with every launch switch OFF, so a missing provider also cannot expose a
// gated (not-yet-launched) term.
const GlossaryContext = createContext<readonly GlossaryEntry[]>(brandGlossaryFor())

export const GlossaryProvider = ({ children }: { children: React.ReactNode }) => {
  const isInternal = useIsInternalUser()
  // VRG-680 — gated entries (Refer & earn) leave the BRAND corpus while their switch is off,
  // so the glossary page, every tooltip, search and Ask's TERMS all lose them together.
  const { switches } = useFeatureSwitches()
  // Identity matters: the corpus is the WeakMap key `glossary.ts` builds its lookup index
  // against, so returning a fresh array each render would rebuild the index every time.
  // `glossaryFor` returns one of two stable module constants, and the memo keeps the
  // context value itself stable so consumers do not re-render on unrelated parent updates.
  const corpus = useMemo(() => glossaryFor(isInternal, switches), [isInternal, switches])

  return <GlossaryContext.Provider value={corpus}>{children}</GlossaryContext.Provider>
}

/** Every entry this reader may see. Use for listing — the glossary page, search. */
export const useGlossaryCorpus = (): readonly GlossaryEntry[] => useContext(GlossaryContext)

/**
 * One entry, resolved against this reader's corpus.
 *
 * Returns `undefined` for an id the reader is not allowed to see, which is the same shape
 * as an id that does not exist — and the two SHOULD be indistinguishable here. A caller
 * that could tell them apart could probe for the existence of internal vocabulary.
 */
export const useGlossaryTerm = (id?: string): GlossaryEntry | undefined => {
  const corpus = useGlossaryCorpus()
  return id ? glossaryTerm(id, corpus) : undefined
}
